<template>
  <Modal :size="ModalSize.LG" :title="props.title">
    <template #activator="{ activatorProps }">
      <Btn
        v-bind="activatorProps"
        :color="BtnColor.TERTIARY"
        :variant="BtnVariant.OUTLINED"
      >
        {{ $t('send-modal.send') }}
        <Icon :icon="SendSvg" :size="IconSize.SM" class="ml-2" />
      </Btn>
    </template>

    <template #content>
      <div class="flex flex-col items-start sm:flex-row flex-wrap gap-5 mt-8">
        <div class="w-full sm:w-max flex items-center justify-center">
          <slot name="object-image" />
        </div>
        <div class="flex-1">
          <slot />
          <p class="text-2xl text-secondary font-bold">
            {{ props.objectToSend.title }}
          </p>
          <p class="text-on-surface-variant">
            {{ props.objectToSend.description }}
          </p>
        </div>
      </div>
      <div
        class="w-full px-3 py-2 mb-8 border border-secondary rounded-full min-h-10 mt-8 flex items-center justify-center text-center text-secondary"
      >
        {{ currentUrl }}
      </div>
    </template>

    <template #actions="{ closeModal }">
      <div class="flex justify-between py-5 px-5 md:px-8 border-t">
        <Btn :size="BtnSize.LG" @click="closeModal">
          {{ $t('common.cancel') }}
        </Btn>

        <div class="flex gap-6">
          <div class="relative">
            <Btn
              :size="BtnSize.LG"
              :variant="BtnVariant.OUTLINED"
              :color="BtnColor.SECONDARY"
              @click="onCopyLink"
            >
              <span class="hidden sm:inline">{{
                $t('send-modal.copy-link')
              }}</span>
              <Icon
                class="inline sm:hidden"
                :icon="CopySvg"
                :size="IconSize.SM"
              />
            </Btn>
            <p v-if="isLinkCopied" class="absolute text-xs text-success">
              {{ $t('send-modal.copy-link') }}
            </p>
          </div>
          <Btn
            :size="BtnSize.LG"
            :variant="BtnVariant.FILLED"
            :color="BtnColor.PRIMARY"
            :to="getMailtoUrl()"
            @click="onEmailSent"
          >
            <span class="hidden sm:inline">{{ $t('send-modal.email') }}</span>
            <Icon :icon="SendSvg" :size="IconSize.SM" class="sm:ml-2" />
          </Btn>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { Icon, IconSize } from '~/components/icon';
import SendSvg from '~/assets/icons/send.svg?component';
import CopySvg from '~/assets/icons/copy.svg?component';
import { computed, ref } from '#imports';
import { ContentMetricType, createContentMetric } from '~/api/metrics';
import { Modal, ModalSize } from '~/components/modals/modal';

type Props = {
  hubSlug?: string;
  contentSlug?: string;
  objectToSend: Record<string, unknown>;
  title: string;
};
const props = defineProps<Props>();

const isLinkCopied = ref(false);

const currentUrl = computed(() => {
  if (globalThis) {
    return globalThis.location.href;
  }
  return '';
});

const onCopyLink = () => {
  if (props.hubSlug && props.contentSlug) {
    createContentMetric(
      props.hubSlug,
      props.contentSlug,
      ContentMetricType.SENT_LINK_COPIED,
    );
  }

  navigator.clipboard.writeText(currentUrl.value);
  isLinkCopied.value = true;
  setTimeout(() => {
    isLinkCopied.value = false;
  }, 1000);
};

const getMailtoUrl = () => {
  const encodedSubject = encodeURIComponent(
    `Link to ${props.objectToSend.title}`,
  );
  const encodedBody = encodeURIComponent(currentUrl.value);
  return `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
};

const onEmailSent = () => {
  if (props.hubSlug && props.contentSlug) {
    createContentMetric(
      props.hubSlug,
      props.contentSlug,
      ContentMetricType.SENT_EMAIL,
    );
  }
};
</script>
